/**
 * THE FILE PICKER. A real one.
 *
 * The submission wizard's media and document steps were, until now, buttons that toggled a boolean.
 * "Attach" set `docs[key] = true`, the review step counted the trues and reported "3 attached", and
 * the asset went to the trust with no title deed on it at all. Nothing was ever picked, uploaded,
 * hashed or stored, and the screen said otherwise at every step.
 *
 * That is a worse failure than a missing feature. An owner who has been told their title deed is
 * attached does not send it again, and a reviewer looking at a submission marked complete does not
 * ask for it. Both of them find out weeks later.
 *
 * So this component does the whole thing and does it honestly:
 *
 *   1. clicking (or dropping on) the area opens the DEVICE file picker
 *   2. the file goes up as multipart, and the SERVER hashes the bytes
 *   3. progress is real - it comes off XMLHttpRequest's upload events, not a timer
 *   4. the uploaded file is listed with its name, size and server-computed hash
 *   5. removing supersedes the row; it never deletes evidence
 *   6. what is accepted comes from the SERVER (`/tokenize/lifecycle/upload-policy`)
 *
 * ## Why XMLHttpRequest and not fetch
 *
 * `fetch` has no upload progress. It has `ReadableStream` request bodies on paper, and neither Safari
 * nor Firefox supports them for this. A four-megabyte title deed on a Nairobi mobile connection takes
 * long enough that a spinner with no number is the difference between "it is working" and "it has
 * frozen", so the one call in this app that needs a progress bar uses the API that has one.
 *
 * This is the only place in the app that bypasses `call()`, and it carries the same Authorization
 * header and the same error taxonomy by hand for exactly that reason.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { api, type UploadPolicy, type UploadedDocument } from '@/lib/api';
import { getToken } from '@/lib/session';

const BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:4000/api/v1';

/** Under UI mocks there is no gateway to POST to, and the fixture layer answers instead. */
const MOCKS_ON = import.meta.env['VITE_UI_MOCKS'] === '1';

export interface UploadState {
  /** 0 to 100. Real, from the upload's own progress events. */
  progress: number;
  fileName: string;
}

/**
 * Send one file, with progress. Resolves to the server's answer, which includes ITS hash of the bytes.
 *
 * A rejection carries the server's own sentence where there is one. "Something went wrong" on a
 * failed title-deed upload tells an owner nothing they can act on, and the server already knows
 * whether the file was too large, the wrong type, or on an application that is not theirs.
 */
export function uploadDocument(
  applicationId: string,
  docType: string,
  file: File,
  onProgress: (percent: number) => void,
  replaces?: string,
): Promise<{ document_id: string; content_hash: string }> {
  if (MOCKS_ON) {
    // The fixture path. Progress is stepped rather than measured, and it is labelled as a fixture in
    // the one place it matters: nothing here reaches a network, so there is nothing real to measure.
    return new Promise((resolve, reject) => {
      let pct = 0;
      const tick = setInterval(() => {
        pct = Math.min(100, pct + 25);
        onProgress(pct);
        if (pct >= 100) {
          clearInterval(tick);
          api.tokenization.upload(applicationId, docType, file, replaces).then(resolve, reject);
        }
      }, 120);
    });
  }

  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('doc_type', docType);
    form.append('file', file);
    if (replaces) form.append('replaces', replaces);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/tokenize/lifecycle/applications/${applicationId}/documents`);

    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    // Deliberately NO Content-Type. The browser sets it with the multipart boundary, and overriding
    // it produces a request the server cannot parse, with an error that blames the file.

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    });

    xhr.addEventListener('load', () => {
      type Body = { message?: string; document_id?: string; content_hash?: string };
      let body: Body | null = null;
      try {
        body = JSON.parse(xhr.responseText) as Body;
      } catch {
        body = null;
      }

      if (xhr.status >= 200 && xhr.status < 300 && body?.document_id) {
        resolve({ document_id: body.document_id, content_hash: body.content_hash ?? '' });
        return;
      }
      reject(new Error(body?.message ?? 'That file could not be uploaded.'));
    });

    xhr.addEventListener('error', () =>
      reject(new Error('The upload did not reach us. Check your connection and try again.')),
    );
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled.')));

    xhr.send(form);
  });
}

/**
 * One document requirement: what it is, what has been uploaded against it, and the picker.
 *
 * The requirement (`label`, `note`, `minCount`) comes from the tokenization product, so this
 * component renders whatever a product asks for and knows nothing about property.
 */
export function DocumentSlot({
  applicationId,
  docType,
  label,
  note,
  required,
  minCount,
  documents,
  accept,
  maxBytes,
  onChanged,
}: {
  applicationId: string;
  docType: string;
  label: string;
  note: string;
  required: boolean;
  minCount: number;
  documents: UploadedDocument[];
  accept: string[];
  maxBytes: number;
  onChanged: () => void | Promise<void>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<UploadState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const mine = documents.filter((d) => d.doc_type === docType);
  const satisfied = !required || mine.filter((d) => d.status !== 'rejected').length >= minCount;

  const send = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      setError(null);

      for (const file of Array.from(files)) {
        // Checked here so the refusal is instant and names the file, and checked AGAIN on the server,
        // which is the one that counts. A client-side limit is a courtesy; `_assert_acceptable` in
        // lipa.adapters.storage is the control.
        if (file.size > maxBytes) {
          setError(
            `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(maxBytes)}.`,
          );
          continue;
        }
        if (accept.length > 0 && !accept.includes(file.type)) {
          setError(
            `${file.name} is a ${file.type || 'file of unknown type'}, which we cannot take.`,
          );
          continue;
        }

        setUploading({ progress: 0, fileName: file.name });
        try {
          await uploadDocument(applicationId, docType, file, (p) =>
            setUploading({ progress: p, fileName: file.name }),
          );
          await onChanged();
        } catch (e) {
          setError(e instanceof Error ? e.message : 'That file could not be uploaded.');
        } finally {
          setUploading(null);
        }
      }

      // Let the same file be picked twice in a row. Without this the input keeps its value and the
      // change event never fires again, which reads as a dead button.
      if (input.current) input.current.value = '';
    },
    [applicationId, docType, accept, maxBytes, onChanged],
  );

  async function remove(documentId: string) {
    setError(null);
    try {
      await api.tokenization.removeDocument(applicationId, documentId);
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That document could not be removed.');
    }
  }

  return (
    <div className="rounded-card border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-ink">
            {label}
            {required ? (
              <span className="ml-1.5 text-micro font-normal text-danger">
                Required{minCount > 1 ? ` (${minCount})` : ''}
              </span>
            ) : (
              <span className="ml-1.5 text-micro font-normal text-ink-subtle">Optional</span>
            )}
          </p>
          <p className="mt-0.5 text-micro text-ink-subtle">{note}</p>
        </div>
        {satisfied && mine.length > 0 ? (
          <span className="shrink-0 text-micro font-medium text-accent">Provided</span>
        ) : null}
      </div>

      {/* THE PICKER. A label wrapping a real <input type="file">, so a keyboard and a screen reader
          reach it the same way a pointer does, and a drop target for the pointer case. */}
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void send(e.dataTransfer.files);
        }}
        className={`mt-3 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-field border border-dashed px-4 py-5 text-center transition ${
          dragging
            ? 'border-primary bg-primary-tint'
            : 'border-border bg-surface-sunken hover:border-primary/50'
        }`}
      >
        <input
          ref={input}
          type="file"
          multiple={minCount > 1}
          accept={accept.join(',')}
          onChange={(e) => void send(e.target.files)}
          className="sr-only"
        />
        <UploadIcon />
        <span className="text-sm font-medium text-ink">
          {mine.length > 0 ? 'Add another file' : 'Choose a file'}
        </span>
        <span className="text-micro text-ink-subtle">
          or drop it here &middot; {accept.map(shortType).join(', ')} &middot; up to{' '}
          {formatBytes(maxBytes)}
        </span>
      </label>

      {uploading ? (
        <div className="mt-3" aria-live="polite">
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate text-micro text-ink-muted">{uploading.fileName}</p>
            <p className="tabular shrink-0 text-micro text-ink-subtle">{uploading.progress}%</p>
          </div>
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-surface-sunken">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${uploading.progress}%` }}
            />
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="mt-2 rounded-field bg-danger/10 px-3 py-2 text-micro text-danger">{error}</p>
      ) : null}

      {mine.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {mine.map((d) => (
            <li
              key={d.document_id}
              className="flex items-center justify-between gap-3 rounded-field bg-surface-sunken px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-micro font-medium text-ink">
                  {d.file_name ?? 'Document'}
                </p>
                <p className="truncate font-mono text-nano text-ink-subtle">
                  {/* The SERVER's hash of the bytes. Shown because it is what makes this evidence,
                      and an owner can check it against their own copy. */}
                  {d.content_hash.slice(0, 16)}...
                  {d.byte_size ? ` · ${formatBytes(d.byte_size)}` : ''}
                </p>
                {d.status === 'rejected' && d.review_note ? (
                  <p className="mt-0.5 text-nano text-danger">{d.review_note}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => void remove(d.document_id)}
                className="shrink-0 rounded-field border border-border px-2.5 py-1 text-nano font-medium text-ink transition hover:bg-surface"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Load the server's upload policy once. Falls back to nothing rather than to a guess. */
export function useUploadPolicy(): UploadPolicy | null {
  const [policy, setPolicy] = useState<UploadPolicy | null>(null);

  useEffect(() => {
    let live = true;
    api.tokenization
      .uploadPolicy()
      .then((p) => {
        if (live) setPolicy(p);
      })
      .catch(() => {
        // Deliberately no default. A hardcoded fallback list is how a picker comes to offer a file
        // type the server refuses, and the refusal would then arrive after the upload.
      });
    return () => {
      live = false;
    };
  }, []);

  return policy;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function shortType(mime: string): string {
  return (
    {
      'application/pdf': 'PDF',
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
      'image/heic': 'HEIC',
      'image/webp': 'WEBP',
    }[mime] ?? mime
  );
}

function UploadIcon() {
  return (
    <svg
      className="h-5 w-5 text-ink-subtle"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.6}
        d="M12 16V4m0 0L8 8m4-4 4 4M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
      />
    </svg>
  );
}
